export interface Project {
  id: string;
  title: string;
  category: string;
  image: string;
  secondaryImage?: string;
  author: string;
  date: string;
  tags: string;
  value: string;
  rating: number;
  description?: string;
  technologies?: string[];
  challenges?: string[];
  results?: string[];
  modules?: Array<{
    id: string;
    label: string;  
  }>;
  details?: {
    overview: string;
    problem: string;
    solution: string;
    impact: string;
    timeline: string;
    team: string;
    client: string;
    budget: string;
    status: string;
  };
}

export interface TranslatedProject {
  id: string;
  title: string;
  category: string;
  description: string;
  technologies: string[];
  challenges: string[];
  results: string[];
  details?: Project['details'];
  modules?: Array<{
    id: string;
    label: string;
  }>;
}

export const PROJECTS: Project[] = [
  {
    id: 'digital-core-platform',
    title: 'Digital Core Platform Modernisation',
    category: 'Cloud Infrastructure',
    image: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=1640&q=80',
    secondaryImage: 'https://images.unsplash.com/photo-1451187580ient-44aead4e3a88?auto=format&fit=crop&w=800&q=80',
    author: 'Rajin Saleh',
    date: '23 December, 2022',
    tags: 'Data Masters',
    value: '$240',
    rating: 4,
    description: 'Modernizing legacy systems with cloud-native architecture',
    technologies: ['AWS', 'Docker', 'Kubernetes', 'React', 'Node.js'],
    challenges: ['Legacy system migration', 'Zero downtime deployment'],
    results: ['50% performance improvement', '99.9% uptime achieved'],
    details: {
      overview:
        'A comprehensive digital transformation initiative that modernized legacy infrastructure systems to cloud-native architecture, enabling scalable and resilient operations.',
      problem:
        "Legacy systems were causing performance bottlenecks, high maintenance costs, and limited scalability. The existing infrastructure couldn't handle increasing user loads and was prone to downtime.",
      solution:
        'Implemented a microservices architecture using Kubernetes orchestration, containerized applications with Docker, and migrated to AWS cloud infrastructure with auto-scaling capabilities.',
      impact:
        'Achieved 50% performance improvement, 99.9% uptime, and reduced operational costs by 40%. The new system can handle 10x more concurrent users.',
      timeline: '6 months (January 2022 - June 2022)',
      team: '12 developers, 3 DevOps engineers, 2 architects, 1 project manager',
      client: 'Fortune 500 Financial Services Company',
      budget: '$2.4M',
      status: 'Completed successfully'
    },
    modules: [
      { id: 'overview', label: 'Overview / Summary' },
      { id: 'architecture', label: 'Architecture' },
      { id: 'deployment', label: 'Deployment / DevOps' },
      { id: 'security', label: 'Security' },
      { id: 'performance', label: 'Performance' },
      { id: 'ui', label: 'UI / Frontend' },
      { id: 'api', label: 'API / Backend' },
      { id: 'other', label: 'Other' }
    ]
  },

  {
    id: 'smart-mobility-network',
    title: 'Smart Mobility Charging Network',
    category: 'IoT & Edge',
    image: 'https://images.unsplash.com/photo-1517142874080-11137e9c619e?auto=format&fit=crop&w=900&q=80',
    secondaryImage: 'https://images.unsplash.com/photo-1593941707882-a5bba14938c7?auto=format&fit=crop&w=800&q=80',
    author: 'Rajin Saleh',
    date: '23 December, 2022',
    tags: 'Data Masters',
    value: '$240',
    rating: 4,
    description: 'IoT-powered electric vehicle charging infrastructure',
    technologies: ['IoT', 'Edge Computing', 'Python', 'MQTT', 'PostgreSQL'],
    challenges: ['Real-time data processing', 'Network reliability'],
    results: ['30% faster charging', '95% network uptime'],
    details: {
      overview:
        'Developed a comprehensive IoT-based electric vehicle charging network with real-time monitoring, predictive maintenance, and intelligent load balancing across multiple charging stations.',
      problem:
        'Traditional charging stations lacked connectivity, real-time monitoring, and predictive maintenance capabilities. Users experienced long wait times and unreliable charging services.',
      solution:
        'Implemented edge computing with IoT sensors, MQTT protocol for real-time communication, machine learning for predictive maintenance, and intelligent load balancing algorithms.',
      impact:
        'Achieved 30% faster charging times, 95% network uptime, and 25% reduction in maintenance costs. Improved user satisfaction by 40%.',
      timeline: '8 months (March 2022 - October 2022)',
      team: '8 IoT engineers, 4 data scientists, 2 hardware engineers, 1 project manager',
      client: 'National Electric Vehicle Infrastructure Company',
      budget: '$1.8M',
      status: 'Completed successfully'
    },
    modules: [
      { id: 'station_firmware', label: 'Station Firmware / Devices' },
      { id: 'iot_connectivity', label: 'IoT Connectivity (MQTT / Network)' },
      { id: 'edge_processing', label: 'Edge Processing' },
      { id: 'telemetry_monitoring', label: 'Telemetry & Monitoring' },
      { id: 'load_balancing', label: 'Load Balancing / Smart Charging' },
      { id: 'maintenance_ml', label: 'Predictive Maintenance (ML)' },
      { id: 'dashboard', label: 'Operations Dashboard' },
      { id: 'billing_payments', label: 'Billing / Payments' },
      { id: 'security', label: 'Security' },
      { id: 'other', label: 'Other' }
    ]
  },

  {
    id: 'fintech-insights-suite',
    title: 'Fintech Insights Suite',
    category: 'Data Analytics',
    image: 'https://images.unsplash.com/photo-1556740749-887f6717d7e4?auto=format&fit=crop&w=900&q=80',
    secondaryImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80',
    author: 'Rajin Saleh',
    date: '23 December, 2022',
    tags: 'Data Masters',
    value: '$240',
    rating: 4,
    description: 'Advanced analytics platform for financial services',
    technologies: ['Python', 'TensorFlow', 'Apache Spark', 'React', 'D3.js'],
    challenges: ['Big data processing', 'Real-time analytics'],
    results: ['40% faster insights', '99.5% accuracy rate'],
    details: {
      overview:
        'Built a comprehensive fintech analytics platform that processes massive financial datasets in real-time, providing actionable insights for risk assessment, fraud detection, and investment decisions.',
      problem:
        "Financial institutions struggled with processing large volumes of transaction data in real-time. Manual analysis was slow, error-prone, and couldn't scale with growing data volumes.",
      solution:
        'Developed a distributed analytics platform using Apache Spark for big data processing, TensorFlow for machine learning models, and real-time streaming with Apache Kafka for live data analysis.',
      impact:
        'Achieved 40% faster insights generation, 99.5% accuracy in fraud detection, and processed 10x more data than previous systems. Reduced false positives by 60%.',
      timeline: '10 months (February 2022 - November 2022)',
      team: '6 data scientists, 4 backend developers, 3 frontend developers, 2 ML engineers, 1 project manager',
      client: 'Major Investment Bank',
      budget: '$3.2M',
      status: 'Completed successfully'
    },
    modules: [
      { id: 'data_ingestion', label: 'Data Ingestion / Pipelines' },
      { id: 'streaming', label: 'Streaming / Real-time' },
      { id: 'spark_jobs', label: 'Spark Jobs / Distributed Compute' },
      { id: 'ml_models', label: 'ML Models (Fraud / Risk)' },
      { id: 'model_monitoring', label: 'Model Monitoring / Drift' },
      { id: 'dashboards', label: 'Dashboards & Visualizations' },
      { id: 'reporting_exports', label: 'Reporting / Exports' },
      { id: 'access_permissions', label: 'Access & Permissions' },
      { id: 'performance', label: 'Performance / Cost' },
      { id: 'other', label: 'Other' }
    ]
  },

  {
    id: 'health-os',
    title: 'Healthcare Operations OS',
    category: 'Digital Transformation',
    image: 'https://images.unsplash.com/photo-1582719478250-04f320207d4c?auto=format&fit=crop&w=900&q=80',
    secondaryImage: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=800&q=80',
    author: 'Rajin Saleh',
    date: '23 December, 2022',
    tags: 'Data Masters',
    value: '$240',
    rating: 4,
    description: 'Comprehensive healthcare management system',
    technologies: ['React', 'Node.js', 'MongoDB', 'Docker', 'AWS'],
    challenges: ['HIPAA compliance', 'Scalable architecture'],
    results: ['60% workflow efficiency', '100% compliance rate'],
    details: {
      overview:
        'Developed a comprehensive healthcare operations management system that digitizes patient workflows, integrates clinical and administrative processes, and ensures full HIPAA compliance while improving operational efficiency.',
      problem:
        'Healthcare facilities struggled with fragmented systems, manual processes, and compliance challenges. Patient data was scattered across multiple systems, leading to inefficiencies and potential compliance violations.',
      solution:
        'Built a unified platform using microservices architecture, implemented end-to-end encryption, role-based access control, and integrated with existing EHR systems while maintaining HIPAA compliance.',
      impact:
        'Achieved 60% improvement in workflow efficiency, 100% HIPAA compliance rate, and 45% reduction in administrative overhead. Improved patient satisfaction scores by 35%.',
      timeline: '12 months (January 2022 - December 2022)',
      team: '10 full-stack developers, 3 security specialists, 2 healthcare consultants, 1 compliance officer, 1 project manager',
      client: 'Regional Healthcare Network (15 hospitals)',
      budget: '$4.5M',
      status: 'Completed successfully'
    },
    modules: [
      { id: 'patient_intake', label: 'Patient Intake' },
      { id: 'scheduling', label: 'Scheduling / Appointments' },
      { id: 'clinical_workflows', label: 'Clinical Workflows' },
      { id: 'admin_billing', label: 'Administration & Billing' },
      { id: 'ehr_integration', label: 'EHR Integration' },
      { id: 'roles_permissions', label: 'Roles & Permissions' },
      { id: 'security_compliance', label: 'Security & Compliance' },
      { id: 'auditing_logs', label: 'Auditing & Logs' },
      { id: 'performance_reliability', label: 'Performance & Reliability' },
      { id: 'other', label: 'Other' }
    ]
  },

  {
    id: 'regtech-co-pilot',
    title: 'RegTech Co-Pilot',
    category: 'AI Automation',
    image: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=900&q=80',
    secondaryImage: 'https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=800&q=80',
    author: 'Rajin Saleh',
    date: '23 December, 2022',
    tags: 'Data Masters',
    value: '$240',
    rating: 4,
    description: 'AI-powered regulatory compliance automation',
    technologies: ['Python', 'OpenAI', 'FastAPI', 'PostgreSQL', 'Redis'],
    challenges: ['Complex regulations', 'AI accuracy'],
    results: ['80% compliance automation', '95% accuracy rate'],
    details: {
      overview:
        'Created an AI-powered regulatory compliance automation platform that uses natural language processing and machine learning to interpret complex regulations, automate compliance checks, and provide explainable AI decisions for financial institutions.',
      problem:
        "Financial institutions faced increasing regulatory complexity with thousands of pages of regulations. Manual compliance checking was time-consuming, error-prone, and couldn't keep up with regulatory changes.",
      solution:
        'Developed an AI system using OpenAI GPT models for natural language understanding, implemented rule-based engines for compliance checking, and created explainable AI interfaces for regulatory auditors.',
      impact:
        'Achieved 80% automation of compliance processes, 95% accuracy in regulation interpretation, and 70% reduction in compliance costs. Reduced regulatory violation risks by 85%.',
      timeline: '9 months (April 2022 - December 2022)',
      team: '5 AI engineers, 3 compliance specialists, 2 backend developers, 1 legal consultant, 1 project manager',
      client: 'Global Investment Management Firm',
      budget: '$2.8M',
      status: 'Completed successfully'
    },
    modules: [
      { id: 'document_ingestion', label: 'Document Ingestion' },
      { id: 'regulation_parsing', label: 'Regulation Parsing (NLP)' },
      { id: 'policy_rules_engine', label: 'Policy / Rules Engine' },
      { id: 'ai_assistant', label: 'AI Assistant (Copilot)' },
      { id: 'explanations_audit', label: 'Explanations & Audit Trail' },
      { id: 'case_management', label: 'Case Management' },
      { id: 'integrations', label: 'Integrations (Data Sources)' },
      { id: 'security', label: 'Security' },
      { id: 'performance_cost', label: 'Performance / Cost' },
      { id: 'other', label: 'Other' }
    ]
  },

  {
    id: 'customer-experience-fabric',
    title: 'Customer Experience Fabric',
    category: 'Experience Engineering',
    image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=900&q=80',
    secondaryImage: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=800&q=80',
    author: 'Rajin Saleh',
    date: '23 December, 2022',
    tags: 'Data Masters',
    value: '$240',
    rating: 4,
    description: 'Unified customer experience platform',
    technologies: ['React', 'TypeScript', 'GraphQL', 'Node.js', 'MongoDB'],
    challenges: ['Multi-channel integration', 'Real-time synchronization'],
    results: ['45% customer satisfaction increase', '70% faster response time'],
    details: {
      overview:
        'Built a unified customer experience platform that integrates multiple touchpoints (web, mobile, call center, retail) with real-time personalization, omnichannel communication, and adaptive user interfaces.',
      problem:
        'Customers experienced fragmented interactions across different channels. Data was siloed, leading to inconsistent experiences and poor customer satisfaction. Support teams lacked unified customer context.',
      solution:
        'Developed a microservices-based platform with GraphQL for unified data access, real-time synchronization across channels, AI-powered personalization engine, and adaptive UI components that respond to user behavior.',
      impact:
        'Achieved 45% increase in customer satisfaction, 70% faster response times, and 60% improvement in cross-channel consistency. Reduced customer churn by 30% and increased revenue per customer by 25%.',
      timeline: '11 months (March 2022 - January 2023)',
      team: '8 frontend developers, 6 backend developers, 3 UX designers, 2 data scientists, 1 project manager',
      client: 'E-commerce Retail Chain (500+ stores)',
      budget: '$3.6M',
      status: 'Completed successfully'
    },
    modules: [
      { id: 'profile_identity', label: 'Customer Profile & Identity' },
      { id: 'personalization', label: 'Personalization Engine' },
      { id: 'omnichannel', label: 'Omnichannel Sync' },
      { id: 'ui_components', label: 'UI Components / Design System' },
      { id: 'graphql_api', label: 'GraphQL API' },
      { id: 'realtime_events', label: 'Real-time Events' },
      { id: 'analytics_insights', label: 'Analytics & Insights' },
      { id: 'performance', label: 'Performance' },
      { id: 'access_permissions', label: 'Access & Permissions' },
      { id: 'other', label: 'Other' }
    ]
  }
];

// Helper functions for easy project management
export const getProjectById = (id: string): Project | undefined => {
  return PROJECTS.find(project => project.id === id);
};

export const getAllProjects = (): Project[] => {
  return PROJECTS;
};

export const getProjectModules = (projectId: string) => {
  const p = getProjectById(projectId);
  return p?.modules ?? [];
};

export const getProjectsByCategory = (category: string): Project[] => {
  return PROJECTS.filter(project => project.category === category);
};

export const getFeaturedProjects = (limit: number = 3): Project[] => {
  return PROJECTS.slice(0, limit);
};

// Type for project layout presets
export interface ProjectLayoutPreset {
  layoutClass: string;
  showSummary: boolean;
  gradientFrom: string;
  gradientTo: string;
  image: string;
}

// Generate layout presets from project data
export const generateLayoutPresets = (): ProjectLayoutPreset[] => {
  return PROJECTS.map((project, index) => ({
    layoutClass: index === 0 
      ? 'col-span-12 lg:col-span-8 min-h-[500px]'
      : index === 1
      ? 'col-span-12 lg:col-span-4 min-h-[500px]'
      : 'col-span-12 sm:col-span-6 lg:col-span-4 min-h-[400px]',
    showSummary: false,
    gradientFrom: index === 0 
      ? 'rgba(2,6,23,0.82)'
      : index === 1
      ? 'rgba(15,23,42,0.85)'
      : 'rgba(15,23,42,0.82)',
    gradientTo: index === 0
      ? 'rgba(12,74,110,0.55)'
      : index === 1
      ? 'rgba(100,116,139,0.55)'
      : 'rgba(37,99,235,0.45)',
    image: project.image
  }));
};

// Translation helper functions
export const getTranslatedProject = (projectId: string, translations: any): TranslatedProject | undefined => {
  const project = getProjectById(projectId);
  if (!project) return undefined;

  const translatedData = translations?.projects?.[projectId];
  if (!translatedData) return undefined;

  return {
    id: project.id,
    title: translatedData.title || project.title,
    category: translatedData.category || project.category,
    description: translatedData.description || project.description || '',
    technologies: translatedData.technologies || project.technologies || [],
    challenges: translatedData.challenges || project.challenges || [],
    results: translatedData.results || project.results || [],
    details: translatedData.details || project.details
  };
};

export const getAllTranslatedProjects = (translations: any): TranslatedProject[] => {
  return PROJECTS.map(project => {
    const translatedData = translations?.projects?.[project.id];
    return {
      id: project.id,
      title: translatedData?.title || project.title,
      category: translatedData?.category || project.category,
      description: translatedData?.description || project.description || '',
      technologies: translatedData?.technologies || project.technologies || [],
      challenges: translatedData?.challenges || project.challenges || [],
      results: translatedData?.results || project.results || [],
      details: translatedData?.details || project.details
    };
  });
};
